/**
 * rerunPreflight — Rerun Wizard Experience (RWX-3).
 *
 * Single source-of-truth for the "X of Y verified · Z unavailable"
 * messaging that the rerun wizard surfaces in THREE different places:
 *
 *   1. The advanced-disclosure collapsed-summary chip
 *      (`ProviderPostureRow` is fed the report-derived posture).
 *   2. The Preflight step body — "Provider data fresh" or the
 *      gap list.
 *   3. The Confirm step recap.
 *
 * Before RWX, each of these three surfaces built its own copy from
 * its own slice of state. The 2026-05-17 Sickel walkthrough caught
 * the three diverging on the same report load: the user saw
 * "0/5 verified · 3 unavailable · 2 unavailable" on the chip
 * simultaneously with "Provider data fresh" on the body (W2). The
 * cure is to consume one computed PreflightSummary everywhere.
 *
 * This module DELIBERATELY consumes `summariseProviderPosture()` from
 * `components/siterag/rerun/ProviderPostureRow.tsx` — the existing
 * relevance-aware classification (which inputs a provider is outside
 * the report type's PROJECTION_BY_TYPE). Re-deriving the provider →
 * dataset map here would fork the truth; we want one map.
 *
 * Pure module — no React, no I/O. Safe to import from anywhere.
 *
 * Pack: docs/prompt-packs/Rerun_Wizard_Experience_Prompt.md
 */
import type { SiteRagReport } from '../types/siteRag';
import type { ReportTypeId } from '../config/reportTypes';
import type { ApiProviderId } from '../config/apiProviders';
import { API_PROVIDERS } from '../config/apiProviders';
import {
  summariseProviderPosture,
  type ProviderPostureItem,
} from '../components/siterag/rerun/ProviderPostureRow';

/**
 * Why a provider is unavailable. Distinguishes the three buckets the
 * user can act on differently:
 *  - 'outside-relevance' — provider's downstream sections aren't part
 *    of the report type's projection. No action needed.
 *  - 'misconfigured'     — auth/key missing. User can supply a key.
 *  - 'transient'         — provider is configured but the last call
 *    failed transiently. User can rerun.
 *
 * Today only 'outside-relevance' is distinguishable from the others
 * via the data shape we have (`summariseProviderPosture` returns the
 * outside-relevance bucket explicitly). 'misconfigured' vs.
 * 'transient' is left for a follow-up that walks `API_PROVIDERS` +
 * the user-keys map; we mark the rest as 'transient' (the safer
 * default for "we don't know — try again").
 */
export type PreflightUnavailableReason =
  | 'outside-relevance'
  | 'misconfigured'
  | 'transient';

/**
 * Three-state summary of a rerun's pre-run readiness.
 *
 *  - 'fresh'   — every in-relevance provider verified; nothing to
 *                resolve before rerunning.
 *  - 'gaps'    — at least one in-relevance provider is unavailable.
 *  - 'partial' — out-of-relevance unavailables exist but no
 *                in-relevance gaps; user can rerun without action,
 *                but the row should still surface the count for
 *                transparency.
 */
export type PreflightState = 'fresh' | 'gaps' | 'partial';

export interface PreflightUnavailable {
  /** Human label (provider displayName). */
  name: string;
  /** Provider id as it appears in `API_PROVIDERS`. */
  providerId: ApiProviderId;
  /** See `PreflightUnavailableReason`. */
  reason: PreflightUnavailableReason;
}

export interface PreflightSummary {
  state: PreflightState;
  /** Count of providers that returned verified data. */
  verifiedCount: number;
  /** Total providers considered (sum of verified + unavailable). */
  totalProviders: number;
  /** Aggregated unavailable list (in-relevance + outside-relevance). */
  unavailable: PreflightUnavailable[];
  /** Canonical short-form string for chip / pill surfaces. */
  headline: string;
  /** Canonical long-form for step body / recap. */
  detail: string;
}

/**
 * Provider → dataset key. Mirrors the PROVIDER_TO_DATASET map used
 * in `FeasibilityRerunPanel`. Local to avoid the import cycle
 * (FeasibilityRerunPanel imports this module via the rerun surface).
 * Verified equality with the rerun panel's map is left to the
 * RWX-AUDIT step.
 */
const PROVIDER_TO_DATASET: Record<ApiProviderId, string> = {
  gemini: 'siteResearch',
  'nrel-solar': 'solar',
  'nrel-wind': 'wind',
  eia: 'electricity',
  airnow: 'airQuality',
};

/**
 * Derive the per-provider verified/unavailable list from a report.
 * Exported so callers can hand the raw items to ProviderPostureRow
 * directly (the rerun panel already does this; RWX-3 lifts the
 * derivation to a shared helper so the chip + step body see the
 * same input).
 */
export function deriveProviderPostureFromReport(
  report: SiteRagReport,
): ProviderPostureItem[] {
  const items: ProviderPostureItem[] = [];
  const unavailableFields = new Set(
    (report.references ?? [])
      .filter(r => typeof r.source === 'string' && r.source.toUpperCase().includes('UNAVAILABLE'))
      .map(r => r.field),
  );
  const datasets = (report.datasets ?? {}) as Record<string, unknown>;
  for (const id of Object.keys(API_PROVIDERS) as ApiProviderId[]) {
    const datasetKey = PROVIDER_TO_DATASET[id];
    const present = datasets[datasetKey] != null;
    const flaggedUnavailable = unavailableFields.has(datasetKey);
    items.push({
      providerId: id,
      status: present && !flaggedUnavailable ? 'verified' : 'unavailable',
    });
  }
  return items;
}

function providerDisplayName(id: ApiProviderId): string {
  return API_PROVIDERS[id]?.displayName ?? id;
}

function formatJoin(names: string[], limit = 2): string {
  if (names.length === 0) return '';
  if (names.length <= limit) return names.join(', ');
  return `${names.slice(0, limit).join(', ')}, +${names.length - limit} more`;
}

/**
 * Compute the single PreflightSummary every rerun-wizard surface
 * reads. The two arguments mirror the pack spec; today only `report`
 * is required (the SED-side gap signal is on `report.sedGaps`).
 * The optional `reportType` override exists so call sites can force
 * the projection (e.g. when reading a typed-panel wrapper).
 */
export function computeRerunPreflight(
  report: SiteRagReport,
  opts?: { reportType?: ReportTypeId },
): PreflightSummary {
  const reportType = (opts?.reportType
    ?? (report.reportType as ReportTypeId | undefined)
    ?? 'feasibility') as ReportTypeId;
  const items = deriveProviderPostureFromReport(report);
  const summary = summariseProviderPosture(items, reportType);

  const unavailable: PreflightUnavailable[] = [];
  for (const providerId of summary.unavailableInRelevance) {
    unavailable.push({
      providerId,
      name: providerDisplayName(providerId),
      // We can't differentiate misconfigured vs. transient here without
      // the user-keys map; default to 'transient' (the action is the
      // same on rerun — try again).
      reason: 'transient',
    });
  }
  for (const providerId of summary.unavailableOutsideRelevance) {
    unavailable.push({
      providerId,
      name: providerDisplayName(providerId),
      reason: 'outside-relevance',
    });
  }

  const inRelevanceCount = summary.unavailableInRelevance.length;
  const outsideCount = summary.unavailableOutsideRelevance.length;

  let state: PreflightState;
  let headline: string;
  let detail: string;

  if (inRelevanceCount === 0 && outsideCount === 0) {
    state = 'fresh';
    headline = 'Provider data fresh';
    detail = 'Provider data fresh — nothing to resolve before rerunning.';
  } else if (inRelevanceCount === 0) {
    state = 'partial';
    headline = `${summary.verified} of ${summary.total} verified`;
    const names = summary.unavailableOutsideRelevance.map(providerDisplayName);
    detail = `${summary.verified} of ${summary.total} verified · ${outsideCount} unavailable (${formatJoin(names)} — outside relevance)`;
  } else {
    state = 'gaps';
    headline = `${summary.verified} of ${summary.total} verified · ${inRelevanceCount} unavailable`;
    const names = summary.unavailableInRelevance.map(providerDisplayName);
    const tail = outsideCount > 0
      ? ` · ${outsideCount} outside relevance`
      : '';
    detail = `${summary.verified} of ${summary.total} verified · ${inRelevanceCount} unavailable (${formatJoin(names)})${tail}`;
  }

  return {
    state,
    verifiedCount: summary.verified,
    totalProviders: summary.total,
    unavailable,
    headline,
    detail,
  };
}
